/* eslint-disable react-hooks/exhaustive-deps */

import classNames from "classnames";
import { ScrollArea } from "components/ScrollArea/ScrollArea";
import Table from "components/Table";
import { Paper } from "helpers/api";
import { SearchResultTextType } from "helpers/search";
import {
  MixpanelActionSource,
  MixpanelCiteType,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import React, { useMemo } from "react";
import { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { CitationGroup } from "./components/CitationGroup";
import Excerpt from "./components/Excerpt";
import useComponentRegistry, {
  useComponentRegistryProps,
  VALID_MODES,
} from "./hooks/useComponentRegistry";
import { ExtendedMarkdown } from "./plugins/react-markdown-registry";
import {
  RemarkCitationGroupProps,
  remarkExcerpt,
  RemarkExcerptProps,
  remarkCitationQuoteNumbering,
  remarkLegacyNumberCitations,
  remarkCitationContextExtraction,
  remarkCitationGrouping,
} from "./plugins/remark";

// Ask Paper cites sources using double braces. When the content is streaming back, the source might render as regular text until the close brackets arrive. So, just trim that content until the close bracket is available.
const trimUnmatchedBraces = (content: string): string => {
  const lastOpenIndex = content.lastIndexOf("{{");
  const lastCloseIndex = content.lastIndexOf("}}");

  if (lastOpenIndex > lastCloseIndex) {
    return content.substring(0, lastOpenIndex);
  }
  return content;
};

type CustomComponents = Components & {
  citationGroup: (props: RemarkCitationGroupProps) => React.ReactNode;
  excerpt: (props: RemarkExcerptProps) => React.ReactNode;
};

interface MarkdownContentProps extends useComponentRegistryProps {
  children: string; // Markdown is a string
  mode?: VALID_MODES; // The mode to render the markdown in
  processCitations?: boolean; // Whether to process citations in the markdown
  onSelectExcerpt?: (text: string, paperId?: string) => void;
  onCitePaper?: (
    paper: Paper,
    citeType: MixpanelCiteType,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onFullTextClick?: (
    paper: Paper,
    linkType: MixpanelFullTextLinkType,
    actionSource: MixpanelActionSource,
    index?: number,
    libraryId?: string
  ) => void;
  sharePaperEventOverride?: (
    paper: Paper,
    index: number,
    shareType: string,
    resultType?: SearchResultTextType
  ) => void;
  /**
   * Called when a citation is found in the analysis during markdown rendering.
   * @param number - The number of the citation.
   * @param quote - Optional quote text.
   * @param quoteIndex - Optional index of the quote within this paper.
   */
  onCitationFoundInAnalysis?: (
    number: number,
    quote?: string,
    quoteIndex?: number
  ) => void;
  showCitationMeterResult?: boolean; // Whether to color the citations based on the consensus meter results
  enableDeferredRender?: boolean; // Whether to enable deferred rendering
  originalUserMessage?: string; // The original user message that was used to generate the analysis
  fullTextPaperIds?: string[]; // List of paper IDs that used full text
}

/*
  Pass content through react-markdown to safely convert it into formatted HTML. 
  Uses a custom component to scan for citations of the format [1],
  and replaces them with a citation component.
*/
const MarkdownContent: React.FC<MarkdownContentProps> = ({
  children,
  papers = [],
  processCitations = true,
  onSelectExcerpt = () => {},
  searchIdParams,
  threadId,
  onClickCitation,
  onCitePaper,
  onFullTextClick,
  sharePaperEventOverride,
  onCitationFoundInAnalysis,
  interactionId,
  isProResearch = false,
  showCitationMeterResult = true,
  showConsensusMeterLoadingState = true,
  mode,
  enableDeferredRender = true,
  originalUserMessage = undefined,
  fullTextPaperIds = [],
}) => {
  // Memoize callback functions to prevent components object from changing
  const memoizedOnCitePaper = React.useCallback(onCitePaper || (() => {}), [
    onCitePaper,
  ]);
  const memoizedOnFullTextClick = React.useCallback(
    onFullTextClick || (() => {}),
    [onFullTextClick]
  );
  const memoizedSharePaperEventOverride = React.useCallback(
    sharePaperEventOverride || (() => {}),
    [sharePaperEventOverride]
  );
  const memoizedOnCitationFoundInAnalysis = React.useCallback(
    onCitationFoundInAnalysis || (() => {}),
    [onCitationFoundInAnalysis]
  );
  const memoizedOnClickCitation = React.useCallback(
    onClickCitation || (() => {}),
    [onClickCitation]
  );
  const memoizedOnSelectExcerpt = React.useCallback(
    (text: string, paperId?: string) => {
      if (onSelectExcerpt) {
        onSelectExcerpt(text, paperId);
      }
    },
    [onSelectExcerpt]
  );

  const components: CustomComponents = React.useMemo(
    () => ({
      p: ({ ...props }) => (
        <p {...props} className="markdown-p break-inside-avoid" />
      ),
      ul: ({ ...props }) => (
        <ul
          {...props}
          className="pl-2 mt-0 mb-1 markdown-ul break-inside-avoid"
        />
      ),
      ol: ({ ...props }) => (
        <ol {...props} className="mt-0 mb-1 markdown-ol marker:text-fg-muted" />
      ),
      li: ({ ...props }) => <li {...props} className="pl-0 mt-0 mb-2" />,
      h1: ({ ...props }) => {
        // Check if any children are strong elements by looking at their key pattern
        const containsBold = React.Children.toArray(props.children).some(
          (child) =>
            React.isValidElement(child) &&
            child.key &&
            typeof child.key === "string" &&
            child.key.includes("strong")
        );

        return (
          <h1
            {...props}
            className={`mt-6 mb-3 text-[1.375rem] first:mt-0 break-after-avoid ${
              containsBold ? "font-normal" : "font-bold"
            }`}
          />
        );
      },
      h2: ({ ...props }) => (
        <h2
          {...props}
          className="mt-6 mb-3 lg-bold first:mt-0 break-after-avoid"
        />
      ),
      h3: ({ ...props }) => (
        <h3
          {...props}
          className="mt-6 mb-3 base-bold first:mt-0 break-after-avoid"
        />
      ),
      h4: ({ ...props }) => (
        <h4
          {...props}
          className="mt-6 mb-3 base-bold first:mt-0 break-after-avoid"
        />
      ),
      h5: ({ ...props }) => (
        <h5
          {...props}
          className="mt-6 mb-3 base-bold first:mt-0 break-after-avoid"
        />
      ),
      h6: ({ ...props }) => (
        <h6
          {...props}
          className="mt-6 mb-3 base-bold first:mt-0 break-after-avoid"
        />
      ),
      pre: ({ ...props }) => <pre {...props} />,
      strong: ({ ...props }) => <strong {...props} className="font-medium" />,
      em: ({ ...props }) => <em {...props} />,
      blockquote: ({ ...props }) => <blockquote {...props} />,
      table: ({ ...props }) => (
        <div className="mt-6 mb-3 break-inside-avoid break-after-avoid">
          {mode === VALID_MODES.export ? (
            <Table {...props} />
          ) : (
            <Table scrollable {...props} />
          )}
        </div>
      ),
      thead: ({ ...props }) => <Table.THead {...props} />,
      tbody: ({ ...props }) => <Table.TBody {...props} />,
      tr: ({ ref, ...props }) => (
        <Table.TR
          {...props}
          ref={
            typeof ref === "string"
              ? undefined
              : (ref as React.Ref<HTMLTableRowElement>)
          }
        />
      ),
      th: ({ ...props }) => (
        <Table.TH
          {...props}
          className={classNames({
            "min-w-[200px]": mode !== VALID_MODES.export,
          })}
        />
      ),
      td: ({ ...props }) => <Table.TD {...props} />,
      br: () => <br />,
      citationGroup: (props: RemarkCitationGroupProps) => {
        const citations = JSON.parse(props.citations);

        return (
          <CitationGroup
            stableKey={props.stableKey}
            isTableCitation={props.isTableCitation}
            citations={citations}
            papers={papers}
            onCitePaper={memoizedOnCitePaper}
            onFullTextClick={memoizedOnFullTextClick}
            sharePaperEventOverride={memoizedSharePaperEventOverride}
            searchId={searchIdParams?.threadSearchId}
            onCitationFoundInAnalysis={memoizedOnCitationFoundInAnalysis}
            onClickCitation={memoizedOnClickCitation}
            referenceLineNumber={props.referenceLineNumber}
            associatedSentence={props.associatedSentence}
            followingPunctuation={props.followingPunctuation}
            showCitationMeterResult={showCitationMeterResult}
            fullTextPaperIds={fullTextPaperIds}
          />
        );
      },
      excerpt: (props: RemarkExcerptProps) => {
        return (
          <Excerpt
            key={props.text}
            text={props.text}
            onSelectExcerpt={memoizedOnSelectExcerpt}
          />
        );
      },
    }),
    [papers, searchIdParams?.threadSearchId]
  );

  const trimmedContent = trimUnmatchedBraces(children);

  // TODO(Aakash) - Once we remove the filiter button, we can remove changes
  // from this PR: https://github.com/Consensus-NLP/common/pull/3741
  // Check if consensus_meter is the first element in the markdown
  const isConsensusMeterFirst = trimmedContent
    .trimStart()
    .startsWith("<consensus_meter");

  const { componentRegistry } = useComponentRegistry({
    papers,
    searchIdParams,
    threadId,
    onClickCitation: onClickCitation,
    onSelectExcerpt: onSelectExcerpt,
    onCitationFoundInAnalysis: memoizedOnCitationFoundInAnalysis,
    interactionId,
    isProResearch,
    showConsensusMeterLoadingState,
    originalUserMessage,
    isConsensusMeterFirst,
    fullTextPaperIds,
  });

  const remarkPlugins = useMemo(() => {
    const plugins: any[] = [remarkGfm, remarkExcerpt];
    if (processCitations) {
      plugins.push([remarkCitationQuoteNumbering, { papers }]); // Add quote indices to paper_cite nodes (1.1, 1.2, etc.)
      plugins.push(remarkLegacyNumberCitations); // Convert [1] to paper_cite nodes
      plugins.push(remarkCitationContextExtraction); // Extract associatedSentence for all paper_cite nodes
      plugins.push(remarkCitationGrouping); // Group adjacent paper_cite nodes
    }
    return plugins;
  }, [processCitations, papers]);

  return (
    <ExtendedMarkdown
      className="prose text-[0.9375rem] sm:text-base leading-normal text-fg-base markdown break-words hyphens-auto"
      components={components}
      registry={componentRegistry}
      remarkPlugins={remarkPlugins}
      skipHtml={false}
      mode={mode}
      enableDeferredRender={enableDeferredRender}
    >
      {trimmedContent}
    </ExtendedMarkdown>
  );
};

export default MarkdownContent;
